// src/router-x/index.js 
import React from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from '../services/contexts/authContext';
import { Wrapper } from '../components';
import { ProtectedRoutes, URLS } from './routes';
import NotFound from '../app/not-found';
import { Button, Spinner } from '@material-tailwind/react';
import { STATIC_USER_ROLES } from '../utils/enums';
import { useScrollRestoration } from '../custom-hooks/use-scroll-restoration';
import { getHomePageRedirectUrl } from '../utils/functions';


function ProtectedRoute({ children, title, roles }) {
  const { user, userDetails, loading } = useAuth();
  const location = useLocation();
  const hasAccess = roles?.includes(userDetails?.role) || false;

  if (loading) {
    return <div className='flex flex-col items-center justify-center h-screen'>
      <Spinner />
    </div>
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (!userDetails) {
    return <div className='flex flex-col items-center justify-center h-screen'>
      <Spinner />
      We are having trouble connecting to the server. Please try refreshing the page.
    </div>
  }

  if (userDetails?.role === STATIC_USER_ROLES.USER && (location.pathname === URLS.ADMIN.ROOT || location.pathname === URLS.ADMIN.HOME)) {
    return <Navigate to={URLS.CUSTOMER.HOME} replace />;
  }

  if (userDetails?.role === STATIC_USER_ROLES.TEAM_MEMBER && (location.pathname === URLS.ADMIN.ROOT || location.pathname === URLS.ADMIN.HOME)) {
    return <Navigate to={URLS.TEAM_MEMBER.HOME} replace />;
  }

  const redirectUrl = getHomePageRedirectUrl(userDetails?.role);

  if (hasAccess) {
    return <Wrapper title={title}>{children}</Wrapper>;
  }
  else {
    return <div className='flex flex-col items-center justify-center h-screen'>
      <h1 className='text-2xl font-bold'>It seems you are lost, Let's get you back on track</h1>
      <p className='text-sm text-gray-500'>For further assistance, please contact Your Administration</p>
      <Link to={redirectUrl}><Button className='mt-4 rounded-full'>Go back on track</Button></Link>
    </div>
  }
}

// Helper function to render nested routes recursively (handles infinite nesting)
const renderNestedRoutes = (routes, parentPath = '') => {
  const renderedRoutes = [];

  routes.forEach((route) => {
    const fullPath = parentPath + route.path;
    const PageComponent = route.page;

    // Add the main route
    renderedRoutes.push(
      <Route
        key={route.id}
        path={fullPath}
        element={
          route.isPrivate ? (
            <ProtectedRoute title={route.name} roles={route.permissibleRoles}>
              <PageComponent />
            </ProtectedRoute>
          ) : (
            <PageComponent />
          )
        }
      />
    );

    // Recursively handle nested routes if they exist
    if (route.nestedRoutes && route.nestedRoutes.length > 0) {
      const nestedRoutes = renderNestedRoutes(route.nestedRoutes, fullPath);
      renderedRoutes.push(...nestedRoutes);
    }
  });

  return renderedRoutes;
};

// Functional component
const RouterX = () => {
  useScrollRestoration();
  return (
    <Routes>
      {renderNestedRoutes(ProtectedRoutes)}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};



export default RouterX;